import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import logoAsset from "@/assets/logo.png.asset.json";
import { useT } from "@/lib/i18n";

export const Route = createFileRoute("/auth/reset-password")({
  ssr: false,
  component: ResetPasswordPage,
  head: () => ({
    meta: [
      { title: "Nastavení hesla — Cerveza.International" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function hashHasRecovery(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hash || "";
  return /(?:^|[#&])type=recovery(?:&|$)/.test(h) || h.includes("type=recovery");
}

// Module-scope recovery detection. The Supabase client parses the URL hash
// asynchronously and strips it from the address bar; both can happen BEFORE
// React mounts and subscribes, which previously left the page stuck on
// "open the link from your email" even though a valid recovery session
// existed. The bulletproof signal is the flag captured in the entry chunk
// (src/lib/recovery-flag.ts, imported by __root); the listeners below cover
// SPA-side recovery as well.
import { hasRecoveryFlag } from "@/lib/recovery-flag";

let recoverySeen = hashHasRecovery() || hasRecoveryFlag();
if (typeof window !== "undefined") {
  supabase.auth.onAuthStateChange((event) => {
    if (event === "PASSWORD_RECOVERY") recoverySeen = true;
  });
}

async function resolveDestination(userId: string): Promise<string> {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  const roles = (data ?? []).map((r) => r.role);
  if (roles.includes("vakant_owner") && !roles.includes("admin") && !roles.includes("moderator")) {
    return "/majitele-vacantu";
  }
  if (roles.includes("admin") || roles.includes("vacanty_admin")) return "/admin";
  return "/moje-podniky";
}

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { t } = useT();
  // `ready` means: we've confirmed a real recovery context, safe to update the
  // password. Setting `ready` on any pre-existing session is unsafe — if the
  // user was already signed in as another account, the form could submit
  // against the OLD session before Supabase swaps to the recovery session.
  const [ready, setReady] = useState<boolean>(() => recoverySeen || hashHasRecovery() || hasRecoveryFlag());
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Only trust the PASSWORD_RECOVERY event, or the presence of a recovery
    // token in the URL hash (Supabase parses it and fires PASSWORD_RECOVERY
    // shortly after). Ignore stale/pre-existing SIGNED_IN sessions here.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) return toast.error(t("authReset.tooShort"));
    if (password !== confirm) return toast.error(t("authReset.mismatch"));
    setSubmitting(true);
    // Re-check we have a session right before submitting.
    const { data: sess } = await supabase.auth.getSession();
    if (!sess.session) {
      setSubmitting(false);
      return toast.error(t("authReset.needLink"));
    }
    const { data, error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success(t("authReset.saved"));
    const dest = data.user ? await resolveDestination(data.user.id) : "/moje-podniky";
    navigate({ to: dest, replace: true });
  };

  return (
    <div className="grid min-h-screen place-items-center bg-background p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src={logoAsset.url} alt="Cerveza.International" className="h-10 w-10 rounded-lg object-cover" />
            <span className="font-serif text-xl">Cerveza.International</span>
          </Link>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-2xl">{t("authReset.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            {!ready ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">{t("authReset.needLink")}</p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/auth" search={{ forgot: "1" } as never}>
                    {t("authReset.requestNewLink") || "Požádat o nový odkaz"}
                  </Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="pw">{t("authReset.newPassword")}</Label>
                  <Input id="pw" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pw2">{t("authReset.passwordAgain")}</Label>
                  <Input id="pw2" type="password" minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                </div>
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? t("authReset.saving") : t("authReset.save")}
                </Button>
                <div className="pt-2 text-center">
                  <Link
                    to="/auth"
                    search={{ forgot: "1" } as never}
                    className="text-xs text-muted-foreground hover:underline"
                  >
                    {t("authReset.requestNewLink") || "Požádat o nový odkaz"}
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
